import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap'
import Line from './MQTTChart'
import RealTimeChart from './RealTimeChart'
import { getDashboardData, getMQTTData, getOfflineChipsData, getOnlineChipsData } from '../../../services/APIServices'
import OnlineOfflineChart from './OnlineOfflineChart'
import MQTTChart from './MQTTChart'
import FleetCharts from './FleetCharts'

// Dashboard rework (Ram, 2026-08-03 + 2026-08-04):
//   - "Users" card counts END users only (backend type:0)
//   - "PCB" -> "Modules", headline = FIELD hardware (DaFlip/DaTwist/
//     DaTwistPro/DaLink); placeholder sockets + retired types shown as subtext
//   - "Relays" card REMOVED (backend dropped the key with B-A8/Q1 — it always
//     rendered 0) and "Panels" card REMOVED (not a launch metric)
//   - "Devices" -> "Appliances", with a BY CLASS breakdown
//   - "Places" card ADDED (v5 domain name; was missing entirely). Values with
//     no legacy fallback key degrade to an em-dash, NEVER a fake 0.
//   - "Curtains" is a SPECIAL category with its own accented card
//   - every card is CLICKABLE and navigates to its screen (see the card list)
//   - "Support Tickets" is a PLACEHOLDER: no ticketing collection exists in
//     the v5 backend (verified 2026-08-03) — support runs outside the system.
const AdminDashboard = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState<any>([]);
    const [moduleBreakdown, setModuleBreakdown] = useState<any>(null);
    const [applianceBreakdown, setApplianceBreakdown] = useState<any>(null);
    const [memoryData, setMemoryData] = useState([]);
    const [onlineChips, setOnlineChips] = useState({});
    const [offlineChips, setOfflineChips] = useState({});
    const [MQTTSmartNodeData, setMQTTSmartNodeData] = useState([])
    const [MQTTWhitelionData, setMQTTWhitelionData] = useState([])
    const [MOTTAlexaData, setMQTTAlexaData] = useState([])
    const [MQTTGoogleData, setMQTTGoogleData] = useState([])

    const getChartData = () => {
        getDashboardData().then((res: any) => {
            // Graceful degrade: an old backend has no modules/appliances
            // payload — fall back to the legacy flat keys.
            const moduleHeadline = res?.modules ? res.modules.field : (res?.pcb || 0);
            const applianceHeadline = res?.appliances ? res.appliances.total : (res?.device || 0);
            setDashboardData([
                // Every card navigates to its screen (Ram, 2026-08-04). The
                // panel has no standalone Places/Appliances screens — places
                // and appliances are reached by drilling through a user's
                // profile, so those cards funnel into /users. Modules has a
                // real global list (/debug-device); Tickets -> /feedback.
                {
                    title: "Users",
                    data: res?.users ? res?.users : 0,
                    to: "/users",
                    icon: <i className="fa-solid fa-users font-size-20"></i>
                },
                {
                    // "Places" is the v5 domain name (a home, office, or any
                    // physical location). Degrades to em-dash, never a fake 0.
                    title: "Places",
                    data: res?.places ?? "—",
                    to: "/users",
                    icon: <i className="fa-solid fa-house font-size-20"></i>
                },
                {
                    title: "Modules",
                    data: moduleHeadline,
                    to: "/debug-device",
                    icon: <i className='bx bx-microchip font-size-24'></i>
                },
                {
                    title: "Appliances",
                    data: applianceHeadline,
                    to: "/users",
                    icon: <i className="fa-solid fa-plug font-size-20"></i>
                },
                {
                    // Curtains as a SPECIAL category (Ram, 2026-08-04): the one
                    // appliance class with hardware semantics (relay-pairing on
                    // 3R1T) — its own card, not just a breakdown column.
                    title: "Curtains",
                    data: res?.appliances?.byClass?.curtain ?? "—",
                    accent: true,
                    to: "/users",
                    icon: <i className="fa-solid fa-person-booth font-size-20"></i>
                },
                {
                    title: "Support Tickets",
                    data: "—",
                    subtext: "No ticketing system connected",
                    to: "/feedback",
                    icon: <i className="fa-solid fa-headset font-size-20"></i>
                }
            ]);
            setModuleBreakdown(res?.modules || null);
            setApplianceBreakdown(res?.appliances || null);
        }).catch((err: any) => {
            console.log("realtime chart error", err);
        })
    }

    const getOnlineChipData = () => {
        getOnlineChipsData().then((res: any) => {
            console.log("online chips chart", res);
            setOnlineChips({
                "All": res.onlineChips,
                "WL11": res.onlineChipsWL1_1,
                "WL12": res.onlineChipsWL1_2,
                "WL13": res.onlineChipsWL1_3,
                "data101": res.onlineChips1_0_1,
                "data201": res.onlineChips2_0_1,
                "data202": res.onlineChips2_0_2,
                "Other": res.onlineChipsOther,
            })
        }).catch((err: any) => {
            console.log("online chart error", err);
        })
    }

    const getOfflineChipData = () => {
        getOfflineChipsData().then((res: any) => {
            console.log("offline chips chart", res);
            setOfflineChips({
                "All": res.offlineChips,
                "WL11": res.offlineChipsWL1_1,
                "WL12": res.offlineChipsWL1_2,
                "WL13": res.offlineChipsWL1_3,
                "data101": res.offlineChips1_0_1,
                "data201": res.offlineChips2_0_1,
                "data202": res.offlineChips2_0_2,
                "Other": res.offlineChipsOther,
            })
        }).catch((err: any) => {
            console.log("offline chart error", err);
        })
    }

    const getMQTTDataCall = () => {
        getMQTTData().then((res: any) => {
            console.log("MQTT chips chart", res);
            setMQTTSmartNodeData(res?.SMARTNODE)
            setMQTTWhitelionData(res?.WHITELION)
            setMQTTAlexaData(res?.ALEXA)
            setMQTTGoogleData(res?.GOOGLE)
        }).catch((err: any) => {
            console.log("offline chart error", err);
        })
    }

    useEffect(() => {
        getChartData();
        // getOnlineChipData();
        // getOfflineChipData();
        // getMQTTDataCall();
    }, [])

    return (
        <>
            <div className="page-content">
                <Row>
                    <Col>

                        <Row>
                            {dashboardData.map((deviceItem: any, index: number) => {
                                return (
                                    <Col key={index} xl="2" lg="4" md="4" sm="6" xs="12">
                                        <Card
                                            role={deviceItem?.to ? "button" : undefined}
                                            onClick={() => deviceItem?.to && navigate(deviceItem.to)}
                                            style={{ borderRadius: 10, boxShadow: "20px", cursor: deviceItem?.to ? "pointer" : undefined, borderLeft: deviceItem?.accent ? "4px solid #7A6FBE" : undefined }}>
                                            <CardBody>
                                                <div
                                                    className="avatar-sm rounded-circle  align-self-center mini-stat-icon float-end"
                                                    style={{ backgroundColor: deviceItem?.accent ? "#7A6FBE" : "#000" }}
                                                >
                                                    <span className="avatar-title rounded-circle" style={{ backgroundColor: deviceItem?.accent ? "#7A6FBE" : "#000" }}>
                                                        {deviceItem?.icon}
                                                    </span>
                                                </div>
                                                <h1 style={{ color: "#5D6679", fontWeight: 500, fontSize: 17, marginBottom: 4 }}>
                                                    {deviceItem?.title}
                                                </h1>
                                                <p style={{ fontSize: 16, color: "#858D9D", marginBottom: 0 }}>{deviceItem?.data}</p>
                                                {deviceItem?.subtext ? (
                                                    <p style={{ fontSize: 11, color: "#B8BFC9", marginBottom: 0 }}>{deviceItem.subtext}</p>
                                                ) : null}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                )
                            })}

                        </Row>
                        <FleetCharts bySku={moduleBreakdown?.bySku || null} moduleMeta={moduleBreakdown} byClass={applianceBreakdown?.byClass || null} />
                    </Col>
                </Row>
            </div >
        </>
    )
}

export default AdminDashboard
